import { ALLOWED_LANGUAGES, type SupportedLanguage } from '../types/video.js';

export interface LanguageDetectionResult {
    language: string | null;
    confidence: number;
    isAllowed: boolean;
}

const TURKISH_INDICATORS = [
    'çocuk', 'çizgi', 'film', 'şarkı', 'oyun', 'eğitici', 'bebek', 'masal',
    'hikaye', 'türkçe', 'ğ', 'ş', 'ı', 'ü', 'ö', 'ç', 've', 'için', 'ile',
    'bir', 'bu', 'çocuklar', 'izle', 'video', 'eğlence', 'kanal', 'bölüm',
];

const DUTCH_INDICATORS = [
    'kinderen', 'kinder', 'liedjes', 'tekenfilm', 'educatief', 'leren', 'baby',
    'peuter', 'kleuter', 'nederlands', 'ij', 'oe', 'voor', 'met', 'het', 'een',
    'van', 'zijn', 'naar', 'ook', 'maar', 'nog', 'meer', 'deze', 'alle', 'aflevering',
];

export function isLanguageAllowed(languageCode: string | undefined | null): boolean {
    if (!languageCode) return false;
    const normalizedCode = languageCode.toLowerCase().split('-')[0];
    return ALLOWED_LANGUAGES.includes(normalizedCode as SupportedLanguage);
}

function normalizeText(text: string): string {
    return text.toLowerCase().trim();
}

function countIndicatorMatches(text: string, indicators: string[]): number {
    const normalizedText = normalizeText(text);
    let count = 0;
    for (const indicator of indicators) {
        if (normalizedText.includes(indicator)) count++;
    }
    return count;
}

export function detectLanguageFromText(title: string, description: string): LanguageDetectionResult {
    const combinedText = `${title} ${description}`;
    if (!combinedText.trim()) {
        return { language: null, confidence: 0, isAllowed: false };
    }

    const turkishMatches = countIndicatorMatches(combinedText, TURKISH_INDICATORS);
    const dutchMatches = countIndicatorMatches(combinedText, DUTCH_INDICATORS);
    const wordCount = combinedText.split(/\s+/).length;
    const minMatchesForConfidence = Math.max(2, Math.floor(wordCount / 10));

    if (turkishMatches > dutchMatches && turkishMatches >= minMatchesForConfidence) {
        const confidence = Math.min(0.9, 0.3 + turkishMatches * 0.1);
        return { language: 'tr', confidence, isAllowed: true };
    }

    if (dutchMatches > turkishMatches && dutchMatches >= minMatchesForConfidence) {
        const confidence = Math.min(0.9, 0.3 + dutchMatches * 0.1);
        return { language: 'nl', confidence, isAllowed: true };
    }

    const totalMatches = turkishMatches + dutchMatches;
    if (totalMatches > 0) {
        return {
            language: turkishMatches > dutchMatches ? 'tr' : 'nl',
            confidence: 0.2 + totalMatches * 0.05,
            isAllowed: false,
        };
    }

    return { language: null, confidence: 0, isAllowed: false };
}

export const LANGUAGE_CONFIDENCE_THRESHOLD = 0.5;
